// Pure Conversation Reducer
// Maps events to state transitions and effects using ConversationDialogueBlueprint

import { 
  ConversationDialogueState, 
  ConversationTrigger, 
  getNextConversationState, 
  getConversationStateMetadata,
  CONVERSATION_STATE_TRANSITIONS
} from '../DialogueManagementConversation/ConversationDialogueBlueprint';
import { 
  TaskDialogueState, 
  TaskTrigger, 
  getNextTaskState, 
  getTaskStateMetadata,
  TASK_STATE_TRANSITIONS
} from '../DialogueManagementTaskClassification/TaskClassificationDialogueBlueprint';
import { isWellDefinedTask, getWellDefinedTaskId, getSubtasksForTask, getWellDefinedTasks, WellDefinedTaskId } from '../DialogueManagementDefinedTaskClassification/wellDefinedTasks';
import { getNextSubtaskState, SubtaskTrigger, SubtaskDialogueState } from '../DialogueManagementDefinedTaskClassification/SubtaskClassificationDialogueBlueprint';
import { getNextDefinedTaskState, DefinedTaskTrigger, DefinedTaskDialogueState, DEFINED_TASK_STATE_TRANSITIONS } from '../DialogueManagementDefinedTaskClassification/DefinedTaskClassificationDialogueBlueprint';
import { Lang, t } from '../../../../i18n';
import { 
  DialogueEvent, 
  DialogueEffect, 
  DialogueContext, 
  ReducerResult, 
  TaskReducerResult,
  SlotSchema
} from './types';

// Helper function to generate allowed intents based on current state
export function getAllowedIntents(
  conversationState: ConversationDialogueState,
  taskState?: TaskDialogueState
): string[] {
  const allowed = new Set<string>();
  // Always include global intents
  ['USER_ASKS_FOR_HELP','USER_SAYS_GOODBYE','USER_SAYS_HELLO','USER_SAYS_SOMETHING_ELSE'].forEach(i => allowed.add(i));
  // Pull from conversation blueprint
  const convTransitions = CONVERSATION_STATE_TRANSITIONS[conversationState] || {};
  Object.keys(convTransitions).forEach(k => allowed.add(k));
  // If in task processing, also pull from task blueprint
  if (conversationState === 'conv_task_processing' && taskState) {
    const taskTransitions = TASK_STATE_TRANSITIONS[taskState] || {};
    Object.keys(taskTransitions).forEach(k => allowed.add(k));
  }
  return Array.from(allowed);
}

// Allowed intents for the well-defined task blueprint (by definedTaskState).
// When we are in conv_defined_task_classification we pass these + definedTaskState so classify uses the right state.
const DEFINED_TASK_TRIGGER_TO_INTENT: Record<string, string> = {
  ABORT: 'USER_SAYS_SOMETHING_ELSE',
  SOMETHING_ELSE: 'USER_SAYS_SOMETHING_ELSE',
  SELECT_SUBTASKS: 'SELECT_SUBTASKS',
  ALL_SUBTASKS_PROCESSED: 'ALL_SUBTASKS_PROCESSED',
  DEFINED_TASK_COMPLETED: 'ALL_TASKS_COMPLETED',
  DEFINED_TASK_ABORTED: 'USER_SAYS_SOMETHING_ELSE',
};

export function getDefinedTaskAllowedIntents(definedTaskState: string): string[] {
  const transitions = DEFINED_TASK_STATE_TRANSITIONS[definedTaskState as DefinedTaskDialogueState];
  if (!transitions) return [];
  const intents = new Set<string>();
  ['USER_ASKS_FOR_HELP', 'USER_SAYS_GOODBYE', 'USER_SAYS_HELLO', 'USER_SAYS_SOMETHING_ELSE'].forEach(i => intents.add(i));
  for (const trigger of Object.keys(transitions)) {
    const intent = DEFINED_TASK_TRIGGER_TO_INTENT[trigger] || trigger;
    intents.add(intent);
  }
  return Array.from(intents);
}

// Helper function to generate slot schema
function getSlotSchema(): SlotSchema {
  return {
    task_description: {
      rules: 'Extract the specific task description with concrete actions, objects, locations, or materials',
      examples: ['cleaned pipes', 'installed valve', 'removed 3m PVC pipe', 'went to basement and measured pipe']
    },
    variant: {
      rules: 'Extract variant/measurement information like dimensions, types, or specifications',
      examples: ['2m x 3m', 'PVC pipe', 'steel valve', 'concrete foundation']
    },
    amount: {
      rules: 'Extract numerical amounts or quantities',
      examples: ['3', '2.5', '10', 'fifteen']
    },
    unit: {
      rules: 'Extract units of measurement',
      examples: ['meters', 'pieces', 'hours', 'kg']
    },
    location: {
      rules: 'Extract location information like rooms, areas, or addresses',
      examples: ['basement', 'kitchen', 'street address', 'building A']
    },
    confirmation: {
      rules: 'Extract confirmation or rejection (true/false)',
      examples: ['yes', 'no', 'correct', 'wrong', 'confirm', 'reject']
    },
    assignement_comment: {
      rules: 'Extract the free-text comment about the assignment (not a specific task). Empty if they only confirm there is nothing to add.',
      examples: ['Traffic delay on site', 'Client asked for extra check', 'OK', 'nothing to add']
    }
  };
}

// Map events to conversation triggers
function mapEventToTrigger(event: DialogueEvent): ConversationTrigger | null {
  // Only handle PARSE_RESULT events
  if (event.type !== 'PARSE_RESULT') {
    return null;
  }
  
  // Map intentId to conversation trigger
  switch (event.intentId) {
        case 'USER_CONFIRMS':
          return 'USER_CONFIRMS';
        case 'USER_REJECTS':
          return 'USER_REJECTS';
        case 'USER_SUBMITS_TASK':
          return 'USER_SUBMITS_TASK';
        case 'USER_WANTS_TO_SUBMIT_TASK':
          return 'USER_WANTS_TO_SUBMIT_TASK';
        case 'USER_ASKS_FOR_HELP':
          return 'USER_ASKS_FOR_HELP';
        case 'USER_ASKS_ABOUT_DEFINED_TASKS':
          return 'USER_ASKS_ABOUT_DEFINED_TASKS';
        case 'USER_SAYS_GOODBYE':
          return 'USER_SAYS_GOODBYE';
        case 'USER_SAYS_HELLO':
          return 'USER_SAYS_HELLO';
        case 'USER_SAYS_SOMETHING_ELSE':
          return 'USER_SAYS_SOMETHING_ELSE';
        case 'USER_CONFIRMS_TASK_SUBMISSION':
          return 'USER_CONFIRMS_TASK_SUBMISSION';
        case 'USER_REJECTS_TASK_SUBMISSION':
          return 'USER_REJECTS_TASK_SUBMISSION';
        case 'USER_RETYPES_TASK':
          return 'USER_RETYPES_TASK';
        case 'USER_WANTS_TO_DELETE_TASK':
          return 'USER_WANTS_TO_DELETE_TASK';
        case 'USER_WANTS_TO_CHANGE_TASK':
          return 'USER_WANTS_TO_CHANGE_TASK';
        case 'USER_ACCEPTS_ALL_TASKS':
          return 'USER_ACCEPTS_ALL_TASKS';
        case 'USER_SAYS_NO':
          return 'USER_SAYS_NO';
        case 'USER_SUBMITS_TASKS':
          return 'USER_SUBMITS_TASKS';
        case 'USER_CANCELS_DELETION':
          return 'USER_CANCELS_DELETION';
        case 'USER_CANCELS_CHANGE':
          return 'USER_CANCELS_CHANGE';
        case 'ALL_TASKS_COMPLETED':
          return 'ALL_TASKS_COMPLETED';
        case 'USER_SUBMITS_ASSIGNEMENT_COMMENT':
          return 'USER_SUBMITS_ASSIGNEMENT_COMMENT';
        case 'TASK_DELETED':
          return 'TASK_DELETED';
        case 'TASK_CHANGED':
          return 'TASK_CHANGED';
        case 'TASK_COMPLETED':
          return 'ALL_TASKS_COMPLETED';
        case 'TASK_ABORTED':
          return 'USER_SAYS_SOMETHING_ELSE';
        default:
          return null;
  }
}

// Map events to task triggers
function mapEventToTaskTrigger(event: DialogueEvent): TaskTrigger | null {
  if (event.type === 'PARSE_RESULT') {
    // Map intentId to task trigger
    switch (event.intentId) {
        case 'SELECT_OPTION':
          return 'SELECT_OPTION';
        case 'SELECT_VARIANT':
          return 'SELECT_VARIANT';
        case 'ENTER_AMOUNT':
          return 'ENTER_AMOUNT';
        case 'ENTER_LOCATION':
          return 'ENTER_LOCATION';
        case 'CONFIRM':
          return 'CONFIRM';
        case 'ABORT':
          return 'ABORT';
        case 'REPHRASE_TASK':
          return 'REPHRASE_TASK';
        case 'REPHRASE_VARIANT':
          return 'REPHRASE_VARIANT';
        case 'INVALID_AMOUNT':
          return 'INVALID_AMOUNT';
        case 'INVALID_OPTION':
          return 'INVALID_OPTION';
        case 'INVALID_VARIANT':
          return 'INVALID_VARIANT';
        case 'SOMETHING_ELSE':
          return 'SOMETHING_ELSE';
        case 'NO_TASKS_FOUND':
          return 'NO_TASKS_FOUND';
        default:
          return null;
    }
  }
  
  if (event.type === 'USER_SELECTION') {
    switch (event.selection.type) {
      case 'task':
        return 'SELECT_OPTION';
      case 'variant':
        return 'SELECT_VARIANT';
      case 'amount':
        return 'ENTER_AMOUNT';
      case 'location':
        return 'ENTER_LOCATION';
      default:
        return null;
    }
  }
  
  return null;
}

/** Work location for tasks: assignment address, else i18n fallback (reducer stays pure given context field). */
function defaultTaskLocationFromContext(context: DialogueContext): string {
  const trimmed = context.assignementDefaultLocation?.trim();
  if (trimmed) return trimmed;
  return t((context.language || 'en') as Lang, 'taskLocationFromAssignementFallback');
}

// Task subflow reducer
function reduceTaskFlow(
  currentState: TaskDialogueState,
  context: DialogueContext,
  event: DialogueEvent
): TaskReducerResult {
  const taskTrigger = mapEventToTaskTrigger(event);
  if (!taskTrigger) {
    return {
      nextState: currentState,
      nextContext: context.taskContext || { slots: {} },
      effects: []
    };
  }

  const nextState = getNextTaskState(currentState, taskTrigger);
  if (!nextState) {
    return {
      nextState: currentState,
      nextContext: context.taskContext || { slots: {} },
      effects: []
    };
  }

  // Task dialogue state transition (logging removed)

  const metadata = getTaskStateMetadata(nextState);
  const effects: DialogueEffect[] = [];

  // Add entry prompt effect
  if (metadata.entryEffectKey) {
    effects.push({
      type: 'ADD_MESSAGE',
      message: {
        id: Math.random().toString(36).substr(2, 9),
        text: '', // Will be filled by effects runner
        sender: 'Assistant',
        timestamp: new Date(),
        isAssistant: true,
        messageType: 'assistant',
        textKey: metadata.entryEffectKey
      }
    });
  }

  // Handle slot collection FIRST (before entry actions)
  // This ensures task object is updated before entry actions like ask_variant run
  const nextTaskContext = { ...context.taskContext, slots: { ...context.taskContext?.slots } };
  const taskUpdates: any = {};
  
  if (event.type === 'PARSE_RESULT') {
    if (event.slots.task_description) {
      nextTaskContext.slots.task_description = event.slots.task_description;
    }
    if (event.slots.variant) {
      nextTaskContext.slots.variant = event.slots.variant;
      taskUpdates.selectedVariantId = event.slots.variant;
    }
    if (event.slots.amount) {
      nextTaskContext.slots.amount = event.slots.amount;
      taskUpdates.amount = event.slots.amount;
    }
    if (event.slots.location) {
      nextTaskContext.slots.location = event.slots.location;
      taskUpdates.location = event.slots.location;
    }
  } else if (event.type === 'USER_SELECTION') {
    // Handle direct user selections (bypassing LLM)
    if (event.selection.type === 'task' && event.selection.value) {
      nextTaskContext.slots.task_description = event.selection.value;
      taskUpdates.selectedTaskId = event.selection.id;
      // Note: selectedTask will be set by the effect handler from the task object
    }
    if (event.selection.type === 'variant' && event.selection.id) {
      nextTaskContext.slots.variant = event.selection.id;
      taskUpdates.selectedVariantId = event.selection.id;
    }
    if (event.selection.type === 'amount' && event.selection.value !== undefined) {
      nextTaskContext.slots.amount = event.selection.value;
      taskUpdates.amount = event.selection.value;
    }
    if (event.selection.type === 'location' && event.selection.value) {
      nextTaskContext.slots.location = event.selection.value;
      taskUpdates.location = event.selection.value;
    }
  }

  if (nextState === 'task_completed' && !nextTaskContext.slots.location) {
    const loc = defaultTaskLocationFromContext(context);
    nextTaskContext.slots.location = loc;
    taskUpdates.location = loc;
  }

  // Emit effect to update the task object BEFORE entry actions
  // This ensures selectedTask is set before ask_variant runs
  if (Object.keys(taskUpdates).length > 0) {
    effects.push({
      type: 'UPDATE_TASK_OBJECT',
      updates: taskUpdates
    });
  }

  // Add entry actions for task state (AFTER updating task object)
  if (metadata.entryActionKeys) {
    metadata.entryActionKeys.forEach(key => {
      effects.push({
        type: 'RUN_ENTRY_ACTION',
        key,
        params: undefined
      });
    });
  }

  // Check required slots and emit ASK effects
  // IMPORTANT: Only emit ASK effects if we're NOT doing an initial state entry
  // Entry actions (entryActionKeys) handle initial state entry - they run automatically via RUN_ENTRY_ACTION
  // ASK effects are for re-asking when slot is still missing after user input
  // 
  // If we just transitioned to a new state (currentState !== nextState), entry actions handle initial prompts
  // If we're staying in the same state, check if slots are missing and emit ASK effects
  const isStateTransition = currentState !== nextState;
  if (!isStateTransition && metadata.requiredSlots) {
    // Only check required slots if we're staying in the same state (user input didn't fill slot)
    for (const slot of metadata.requiredSlots) {
      if (!nextTaskContext.slots[slot]) {
        switch (slot) {
          case 'task_description':
            effects.push({ type: 'ASK_TASK_DESCRIPTION', prompt: 'Please describe your task' });
            break;
          case 'variant':
            effects.push({ type: 'ASK_VARIANT', options: [] }); // Options filled by effects runner
            break;
          case 'amount':
            effects.push({ type: 'ASK_AMOUNT', prompt: 'Please provide the amount' });
            break;
          case 'confirmation':
            effects.push({ type: 'ASK_CONFIRMATION', prompt: 'Are you sure?' });
            break;
        }
        break; // Only ask for the first missing slot
      }
    }
  }

  // Handle terminal states
  let parentTrigger: string | undefined;
  if (nextState === 'task_completed') {
    parentTrigger = 'ALL_TASKS_COMPLETED';
    // Emit effect to mark the task as completed in the conversation object
    effects.push({
      type: 'MARK_TASK_COMPLETED',
      taskData: {
        slots: nextTaskContext.slots,
        taskState: nextState
      }
    });
  } else if (nextState === 'task_aborted') {
    parentTrigger = 'USER_SAYS_SOMETHING_ELSE'; // Return to something else flow
    // Emit effect to mark the task as aborted
    effects.push({
      type: 'MARK_TASK_ABORTED'
    });
  }

  return {
    nextState,
    nextContext: nextTaskContext,
    effects,
    parentTrigger
  };
}

// Main conversation reducer
export function reduceConversation(
  currentState: ConversationDialogueState,
  context: DialogueContext,
  event: DialogueEvent
): ReducerResult {
  let nextState = currentState;
  let nextContext = { ...context };
  const effects: DialogueEffect[] = [];
  
  // 1. Handle NEW_MESSAGE from user
  if (event.type === 'NEW_MESSAGE' && event.message.text && event.message.text.trim()) {
    // Lightweight debug logging; can be toggled or removed in production
    console.log('🔴 [REDUCER] NEW_MESSAGE received', {
      messageText: event.message.text,
      messageTaskId: (event.message as any).taskId,
      currentState,
      definedTaskState: context.definedTaskState,
      taskState: context.taskState,
      hasSubtaskData: !!context.subtaskData,
      currentSubtaskIndex: context.subtaskData?.currentSubtaskIndex,
      wellDefinedTaskId: context.wellDefinedTaskId
    });
    
    // Use trimmed text so leading/trailing space cannot break matching
    const msgTextTrimmed = (event.message.text || '').trim();
    const isSubtaskSelectionText = /^Selected subtasks:\s*.+/i.test(msgTextTrimmed) || /^Geselecteerde subtaken:\s*.+/i.test(msgTextTrimmed);
    
    // Subtask-selection text in wrong state: never call LLM, never show "Could you please clarify"
    if (isSubtaskSelectionText && currentState !== 'conv_defined_task_classification') {
      return { nextState, nextContext, effects };
    }
    
    // Special handling: Check if this is a subtask submission in conv_defined_task_classification
    if (currentState === 'conv_defined_task_classification') {
      // Support both English and Dutch formats (case-insensitive, use trimmed text)
      const subtaskMatchEn = msgTextTrimmed.match(/Selected subtasks:\s*(.+)/i);
      const subtaskMatchNl = msgTextTrimmed.match(/Geselecteerde subtaken:\s*(.+)/i);
      const subtaskMatch = subtaskMatchEn || subtaskMatchNl;
      if (subtaskMatch) {
        // Extract subtask IDs (comma-separated)
        const subtaskIds = subtaskMatch[1].split(',').map(id => id.trim()).filter(id => id);
        
        // CRITICAL: Create ONE TASK OBJECT per selected subtask and append to conversation
        // Each subtask becomes its own task object - no "subtask" concept in the data structure
        const wellDefinedTaskIdForSubtasks = nextContext.wellDefinedTaskId || context.wellDefinedTaskId;
        const taskObjects: any[] = [];
        
        // Get subtask names for user descriptions
        let subtaskNames: Record<string, string> = {};
        if (wellDefinedTaskIdForSubtasks) {
          try {
            const subtasks = getSubtasksForTask(wellDefinedTaskIdForSubtasks as WellDefinedTaskId, context.language as Lang);
            subtaskNames = subtasks.reduce((acc: Record<string, string>, st: any) => {
              acc[st.id] = st.name || st.description || st.id;
              return acc;
            }, {});
          } catch (err) {
            // Could not get subtask names
          }
        }
        
        // Create one task object per selected subtask
        subtaskIds.forEach((subtaskId) => {
          const initialName = subtaskNames[subtaskId] || subtaskId;
          const taskObj = {
            id: Math.random().toString(36).substr(2, 9), // Internal ID for React keys
            taskId: subtaskId, // THE SINGLE NUMERICAL ID - updated as we navigate deeper
            userDescription: initialName, // Use subtask name as user description
            name: initialName, // Accumulating name path: "mainName; name2; name3"
            finalName: undefined as string | undefined, // Final name when leaf is reached
            dialogueState: 'leaf_validation' as const, // Start with leaf validation
            isProcessingDialogue: false, // Will be set to true when we start processing
            isSubmitted: false,
            wellDefinedTaskId: wellDefinedTaskIdForSubtasks
          };
          console.log('➕ [TASK_CREATION] [REDUCER] Creating task object for subtask', {
            taskId: subtaskId,
            name: initialName,
            wellDefinedTaskId: wellDefinedTaskIdForSubtasks,
            id: taskObj.id
          });
          taskObjects.push(taskObj);
        });
        
        console.log('➕ [TASK_CREATION] [REDUCER] Created task objects for subtasks', {
          count: taskObjects.length,
          taskIds: taskObjects.map((t: any) => t.taskId),
          wellDefinedTaskId: wellDefinedTaskIdForSubtasks
        });
        
        // Add all task objects to conversation
        // NOTE: Parent removal will be handled in UIEffectsHandler when tasks are added
        effects.push({
          type: 'ADD_TASKS_TO_CONVERSATION',
          tasks: taskObjects
        });
        
        // Store subtask IDs in context for tracking which tasks we're processing
        nextContext.subtaskData = {
          selectedSubtasks: subtaskIds,
          currentSubtaskIndex: 0,
          processedSubtasks: [] // Not used anymore - each task is its own object
        };
        
        // Transition to subtasks_processing state
        nextContext.definedTaskState = 'subtasks_processing';
        
        // Get subtask name for the processing message - use the same method as option selection
        const firstSubtaskId = subtaskIds[0];
        let subtaskName = firstSubtaskId; // Fallback to ID
        
        // Get name from task object properties - NEVER parse from message text
        // First, try task object (created upfront or from previous step)
        if (taskObjects.length > 0) {
          const firstTask = taskObjects[0];
          if (firstTask.userDescription && firstTask.userDescription !== firstSubtaskId) {
            subtaskName = firstTask.userDescription;
          } else if (firstTask.name && firstTask.name !== firstSubtaskId) {
            subtaskName = firstTask.name;
          }
        }
        
        // If not found in task object, try message.taskDescription (set when creating the message)
        if (subtaskName === firstSubtaskId && (event.message as any).taskDescription) {
          subtaskName = (event.message as any).taskDescription;
        }
        
        // If still not found, try well-defined tasks lookup
        if (subtaskName === firstSubtaskId && wellDefinedTaskIdForSubtasks) {
          try {
            const subtasks = getSubtasksForTask(wellDefinedTaskIdForSubtasks as WellDefinedTaskId, context.language as Lang);
            const currentSubtask = subtasks.find((s: any) => s.id === firstSubtaskId);
            if (currentSubtask) {
              subtaskName = currentSubtask.name || currentSubtask.description || firstSubtaskId;
            }
          } catch (err) {
            // Could not get subtask name
          }
        }
        
        // If still not found and it's a well-defined task, try well-defined tasks lookup
        if (subtaskName === firstSubtaskId && wellDefinedTaskIdForSubtasks) {
          try {
            const subtasks = getSubtasksForTask(wellDefinedTaskIdForSubtasks as WellDefinedTaskId, context.language as Lang);
            const currentSubtask = subtasks.find((s: any) => s.id === firstSubtaskId);
            if (currentSubtask) {
              subtaskName = currentSubtask.name || currentSubtask.description || firstSubtaskId;
            }
          } catch (err) {
            // Could not get subtask name
          }
        }
        
        // Find the first task object (created from first subtask) and start processing it
        // The task objects were just added, so we need to mark the first one as processing
        // and start leaf validation
        if (taskObjects.length > 0) {
          const firstTask = taskObjects[0];
          
          // Mark first task as processing
          effects.push({
            type: 'UPDATE_TASK_OBJECT',
            updates: {
              isProcessingDialogue: true
            },
            taskId: firstTask.id
          });
          
          // Add processing message for the first subtask
          // CRITICAL: Mark this as a new subtask starting for visual styling
          const langForProcessing = context.language || 'en';
          effects.push({
            type: 'ADD_MESSAGE',
            message: {
              id: Math.random().toString(36).substr(2, 9),
              text: t(langForProcessing as Lang, 'processingSubtaskNow', { taskDescription: subtaskName }),
              sender: 'Assistant',
              timestamp: new Date(),
              isAssistant: true,
              messageType: 'assistant',
              isNewSubtask: true // Flag to indicate this is a new subtask starting
            }
          });
          
          // Start processing first task by checking if it's a leaf
          effects.push({
            type: 'CHECK_SUBTASK_LEAF',
            subtaskId: firstSubtaskId,
            language: context.language,
            taskId: firstTask.id // Pass task ID so we know which task we're processing
          });
        }
        
        return { nextState, nextContext, effects };
      }
      
      // Special handling: Check if user selected an option in option_selection state
      // CRITICAL: This must happen BEFORE the LLM call to bypass it
      // Check if message has taskId (indicates option selection) OR if we're in option_selection state
      const definedTaskState = context.definedTaskState || nextContext.definedTaskState || 'subtasks_selection';
      const hasTaskId = !!event.message.taskId;
      const isInOptionSelection = definedTaskState === 'option_selection';
      
      // Option-by-text: user typed "1" or a description; match to currentChildren and treat as selection
      if (currentState === 'conv_defined_task_classification' && isInOptionSelection && !hasTaskId) {
        const children = context.subtaskData?.currentChildren as Array<{ id: string; description?: string; dutch_description?: string }> | undefined;
        if (children && children.length > 0 && event.message.text) {
          const txt = (event.message.text || '').trim().toLowerCase();
          const asNum = parseInt(txt, 10);
          let matched: { id: string; description?: string; dutch_description?: string } | undefined;
          if (!Number.isNaN(asNum) && asNum >= 1 && asNum <= children.length) {
            matched = children[asNum - 1];
          }
          if (!matched) {
            matched = children.find((c) => (c.description || '').toLowerCase().trim() === txt);
          }
          if (!matched) {
            matched = children.find((c) => {
              const d = (c.description || '').toLowerCase();
              return d.includes(txt) || txt.includes(d);
            });
          }
          if (matched) {
            const desc = context.language === 'nl' ? (matched.dutch_description || matched.description) : matched.description;
            return reduceConversation(currentState, nextContext, {
              type: 'NEW_MESSAGE',
              message: { ...event.message, taskId: matched.id, taskDescription: desc || matched.description } as typeof event.message
            });
          }
        }
      }
      
      // Special handling: variant selection in defined-task flow – never call LLM when a variant button was used
      const isInVariantSelection = definedTaskState === 'variant_selection';
      if (currentState === 'conv_defined_task_classification' && isInVariantSelection) {
        const variantIdFromMessage = (event.message as any).variantId as string | undefined;
        // IMPORTANT: Only treat a variant as selected when it came from the UI (variantId on the message).
        // Never parse the human-readable label from message text as a "variantId" because it can be
        // something like "height: 1.5 m < height < 2 m" and would overwrite task.taskId.
        if (variantIdFromMessage) {
          const subtaskDataForVariant = nextContext.subtaskData || context.subtaskData;
          let currentSubtaskIdForVariant: string | undefined;
          if (subtaskDataForVariant && subtaskDataForVariant.selectedSubtasks && subtaskDataForVariant.currentSubtaskIndex !== undefined) {
            currentSubtaskIdForVariant = subtaskDataForVariant.selectedSubtasks[subtaskDataForVariant.currentSubtaskIndex];
          }
          const resolvedSubtaskId = currentSubtaskIdForVariant || variantIdFromMessage;
          const resolvedVariantId = variantIdFromMessage;
          if (resolvedSubtaskId) {
            return reduceConversation(currentState, nextContext, {
              type: 'SUBTASK_VARIANT_SELECTED',
              subtaskId: resolvedSubtaskId,
              variantId: resolvedVariantId
            });
          }
        }
      }
      
      console.log('🔴 [REDUCER] Checking option selection conditions', {
        definedTaskState,
        hasTaskId,
        isInOptionSelection,
        currentState,
        messageTaskId: event.message.taskId,
        messageText: event.message.text?.substring(0, 50)
      });
      
      // If we have a taskId in the message and we're in defined task classification, it's likely an option selection
      if ((isInOptionSelection || hasTaskId) && currentState === 'conv_defined_task_classification') {
        console.log('🔴 [REDUCER] OPTION SELECTION DETECTED - Processing option selection');
        // Get option ID from the message object (taskId property) - this is the correct way
        // The message.taskId is set when the option is selected in handleSelectTaskWrapper
        let selectedOptionId: string | null = null;
        
        if (event.message.taskId) {
          // Use the taskId from the message object (most reliable)
          selectedOptionId = event.message.taskId;
        } else {
          // Fallback: try to extract from message text (shouldn't be needed, but keep as backup)
          // Support both English and Dutch formats
          const optionIdMatchEn = event.message.text.match(/Task ID: ([A-Za-z0-9]+)/);
          const optionIdMatchNl = event.message.text.match(/Taak ID: ([A-Za-z0-9]+)/);
          const optionIdMatch = optionIdMatchEn || optionIdMatchNl;
          if (optionIdMatch) {
            selectedOptionId = optionIdMatch[1];
          }
        }
        
        if (selectedOptionId) {
          console.log('🔴 [REDUCER] selectedOptionId found:', selectedOptionId);
          
          // With the new architecture, we need to find the active task object
          // Since we can't access conversation directly, we'll use subtaskData to track which task we're processing
          const subtaskData = context.subtaskData || nextContext.subtaskData;
          
          console.log('🔴 [REDUCER] Checking subtaskData', {
            hasSubtaskData: !!subtaskData,
            currentSubtaskIndex: subtaskData?.currentSubtaskIndex,
            hasSelectedSubtasks: !!(subtaskData?.selectedSubtasks && subtaskData.selectedSubtasks.length > 0)
          });
          
          // If we have subtaskData, use it to find the current task entry
          if (subtaskData && subtaskData.currentSubtaskIndex !== undefined) {
            console.log('🔴 [REDUCER] Using subtaskData path (old subtask flow)');
            
            // CRITICAL: Find the entry for the current subtask by ORIGINAL subtaskId
            // Entries are pre-created when subtasks are submitted, so it should always exist
            // As we narrow down through children, we update the subtaskId to the current child ID
            const currentSubtaskId = subtaskData.selectedSubtasks?.[subtaskData.currentSubtaskIndex || 0];
            const processedSubtasks = [...(subtaskData.processedSubtasks || [])];
            
            // Find entry by ORIGINAL subtaskId (entries are pre-created, so this should always exist)
            // We need to find the entry that corresponds to the current subtask being processed
            // Match by checking if the entry's subtaskId matches the original OR if it's a child/variant of it
            // Since entries are pre-created with original IDs, we should find it by original ID
            let subtaskEntry = processedSubtasks.find((s: any, index: number) => {
              // Match by index (entries are created in the same order as selectedSubtasks)
              if (index === subtaskData.currentSubtaskIndex) {
                return true;
              }
              // Fallback: match by original subtaskId (in case order doesn't match)
              // Check if this entry's original subtaskId (before narrowing) matches
              // We can't directly check "original" ID, but we can check if it's the entry for current index
              return false;
            });
            
            // If not found by index, try to find by matching the original subtaskId
            // This handles cases where entries might have been narrowed down already
            if (!subtaskEntry) {
              // Find the entry that was originally created for this subtask
              // Since we pre-create entries in order, the entry at currentSubtaskIndex should be the right one
              const entryIndex = subtaskData.currentSubtaskIndex || 0;
              if (entryIndex < processedSubtasks.length) {
                subtaskEntry = processedSubtasks[entryIndex];
              }
            }
            
            // If still not found (shouldn't happen), create it as fallback
            if (!subtaskEntry) {
              subtaskEntry = { subtaskId: currentSubtaskId };
              processedSubtasks.push(subtaskEntry);
            }
            
            // Update the subtaskId to the newly selected option (narrowing down)
            // This replaces the previous subtaskId - we're narrowing down the path
            subtaskEntry.subtaskId = selectedOptionId;
            
            // Update the current subtask to be the selected option (recursive validation)
            // Store the selected option ID in currentChildId for the next validation step
            // NOTE: currentSubtaskIndex and currentSubtaskId remain unchanged - they always point to the original subtask
            // This ensures that when we recursively validate options, we always update the same entry
            const updatedSubtaskData = {
              ...subtaskData,
              currentChildId: selectedOptionId, // Track which option we're validating (for recursive validation)
              processedSubtasks: processedSubtasks // Update with the modified entry (same entry, just updated optionId)
            };
            nextContext.subtaskData = updatedSubtaskData;
            
            // Also update the task object with the updated processedSubtasks
            effects.push({
              type: 'UPDATE_TASK_OBJECT',
              updates: {
                processedSubtasks: processedSubtasks
              }
            });
            
            // Get option description - PRIORITY: Use the EXACT description from the button (message.taskDescription)
            // This is the source of truth - it's what the user actually saw and clicked
            let optionName = selectedOptionId; // Fallback to ID
            
            // FIRST: Try message.taskDescription (the EXACT text shown in the selection button)
            if ((event.message as any).taskDescription) {
              optionName = (event.message as any).taskDescription;
            } else {
              // Fallback: try to get from stored children in context
              // Use GLOBAL language setting - tasks don't have their own language
              const globalLang = (context.language || 'en') as Lang;
              const subtaskDataForLookup = context.subtaskData || nextContext.subtaskData;
              if (subtaskDataForLookup?.currentChildren) {
                const selectedChild = subtaskDataForLookup.currentChildren.find(
                  (child: any) => child.id === selectedOptionId
                );
                if (selectedChild) {
                  // Use global language to get correct description
                  optionName = globalLang === 'nl' 
                    ? (selectedChild.dutch_description || selectedChild.description || selectedOptionId)
                    : (selectedChild.description || selectedOptionId);
                }
              }
            }
            
            // If still not found, try well-defined tasks lookup
            if (optionName === selectedOptionId) {
              const wellDefinedTaskIdForLookup = context.wellDefinedTaskId || nextContext.wellDefinedTaskId;
              if (wellDefinedTaskIdForLookup) {
                try {
                  const subtasks = getSubtasksForTask(wellDefinedTaskIdForLookup as WellDefinedTaskId, context.language as Lang);
                  const currentSubtask = subtasks.find((s: any) => s.id === selectedOptionId);
                  if (currentSubtask) {
                    optionName = currentSubtask.name || currentSubtask.description || selectedOptionId;
                  }
                } catch (err) {
                  // Could not get subtask name
                }
              }
            }
            
            // If still not found, the task object should have userDescription set when created
            // The effect handler will use the task object's properties, so we can fall back to ID here
            
            // CRITICAL: Update task object's taskId, userDescription, and name
            // Append to name path: "mainName; name2; name3"
            // The effect handler will append to existing name if it exists
            const nameUpdate = optionName !== selectedOptionId ? optionName : undefined;
            console.log('📝 [NAME] Option selection - updating name:', { selectedOptionId, optionName, nameUpdate, willAppend: !!nameUpdate });
            const updates: any = {
              dialogueState: 'leaf_validation',
              taskId: selectedOptionId, // Update to the selected option ID (narrowing down)
            };
            if (nameUpdate) {
              updates.userDescription = nameUpdate;
              updates.name = nameUpdate; // Will be appended to existing name by effect handler
            }
            effects.push({
              type: 'UPDATE_TASK_OBJECT',
              updates
            });
            
            // Add processing message for the selected option
            const langForOptionProcessing4 = context.language || 'en';
            effects.push({
              type: 'ADD_MESSAGE',
              message: {
                id: Math.random().toString(36).substr(2, 9),
                text: t(langForOptionProcessing4 as Lang, 'processingSubtaskNow', { taskDescription: optionName }),
                sender: 'Assistant',
                timestamp: new Date(),
                isAssistant: true,
                messageType: 'assistant'
              }
            });
            
            // Validate the selected option to determine next state:
            // 1. If has variants → variant_selection
            // 2. If is a leaf → amount_collection
            // 3. If has children → option_selection (go deeper)
            effects.push({
              type: 'CHECK_SUBTASK_LEAF',
              subtaskId: selectedOptionId,
              language: context.language
              // taskId will be determined by the effect handler (finds active task by isProcessingDialogue)
            });
            
            
            return { nextState, nextContext, effects };
        } else {
          console.log('🔴 [REDUCER] No subtaskData - using multi-select/new architecture path', {
            selectedOptionId,
            messageTaskId: event.message.taskId,
            currentState,
            taskState: context.taskState,
            definedTaskState: context.definedTaskState,
            wellDefinedTaskId: context.wellDefinedTaskId,
            note: 'Effect handler will find active task by isProcessingDialogue from ConversationObj'
          });
            
            // No subtaskData - this might be the new architecture where each task is its own object
            // OR we're in multi-select mode where we need to create NEW task objects per selection
            
            // Check if we're in multi-select mode (when Continue is clicked with multiple selections)
            // In multi-select mode, we create a NEW task object for each selected option
            // instead of updating the existing one
            
            // Get option description - PRIORITY: Use the EXACT description from the button (message.taskDescription)
            // This is the source of truth - it's what the user actually saw and clicked
            let optionName = selectedOptionId; // Fallback to ID
            
            // FIRST: Try message.taskDescription (the EXACT text shown in the selection button)
            if ((event.message as any).taskDescription) {
              optionName = (event.message as any).taskDescription;
            } else {
              // Fallback: try to get from stored children in context
              // Use GLOBAL language setting - tasks don't have their own language
              const globalLang3 = (context.language || 'en') as Lang;
              const subtaskDataForLookup3 = context.subtaskData || nextContext.subtaskData;
              if (subtaskDataForLookup3?.currentChildren) {
                const selectedChild = subtaskDataForLookup3.currentChildren.find(
                  (child: any) => child.id === selectedOptionId
                );
                if (selectedChild) {
                  // Use global language to get correct description
                  optionName = globalLang3 === 'nl' 
                    ? (selectedChild.dutch_description || selectedChild.description || selectedOptionId)
                    : (selectedChild.description || selectedOptionId);
                }
              }
            }
            
            // If still not found, try well-defined tasks lookup
            if (optionName === selectedOptionId) {
              const wellDefinedTaskIdForLookup2 = context.wellDefinedTaskId || nextContext.wellDefinedTaskId;
              if (wellDefinedTaskIdForLookup2) {
                try {
                  const subtasks = getSubtasksForTask(wellDefinedTaskIdForLookup2 as WellDefinedTaskId, context.language as Lang);
                  const currentSubtask = subtasks.find((s: any) => s.id === selectedOptionId);
                  if (currentSubtask) {
                    optionName = currentSubtask.name || currentSubtask.description || selectedOptionId;
                  }
                } catch (err) {
                  // Could not get subtask name
                }
              }
            }
            
            // If still not found, the task object should have userDescription set when created upfront
            // The effect handler will use the task object's properties, so we can fall back to ID here
            
            // CRITICAL: In multi-select mode, task objects are created upfront in handleContinueSelectionWrapper
            // So we just need to update the existing task object and start processing it
            // Get wellDefinedTaskId from context (stored when task was created)
            const wellDefinedTaskId = nextContext.wellDefinedTaskId || context.wellDefinedTaskId;
            
            console.log('🟣 [NEW_MESSAGE] Processing task in leaf_validation (multi-select mode) - BEFORE UPDATE', {
              selectedOptionId,
              optionName,
              wellDefinedTaskId,
              messageTaskId: event.message.taskId,
              messageText: event.message.text,
              note: 'Task will be found by isProcessingDialogue in effect handler (tasks are in ConversationObj, not context)'
            });
            
            // CRITICAL: Update the task object's taskId to the selected option ID (this is the FastAPI task ID)
            // The effect handler will find the task by isProcessingDialogue (the currently active task)
            // Then it will update the taskId to the new selectedOptionId
            // IMPORTANT: Don't pass effect.taskId - that's for LOOKUP. We want to UPDATE the taskId property.
            effects.push({
              type: 'UPDATE_TASK_OBJECT',
              updates: {
                dialogueState: 'leaf_validation',
                isProcessingDialogue: true,
                taskId: selectedOptionId, // Update taskId (FastAPI ID) to the selected option - this updates the property!
                userDescription: optionName,
                name: undefined // Will be appended by effect handler if existing name exists
              }
              // Don't pass effect.taskId - let effect handler find by isProcessingDialogue (fallback at line 436)
              // The effect handler will find the active task (by isProcessingDialogue) and update its taskId to selectedOptionId
            });
            
            console.log('🟣 [NEW_MESSAGE] Dispatched UPDATE_TASK_OBJECT effect - will find task by isProcessingDialogue, then update its taskId to', selectedOptionId);
            
            // Add processing message for the selected option
            const langForOptionProcessing1 = context.language || 'en';
            effects.push({
              type: 'ADD_MESSAGE',
              message: {
                id: Math.random().toString(36).substr(2, 9),
                text: t(langForOptionProcessing1 as Lang, 'processingSubtaskNow', { taskDescription: optionName }),
                sender: 'Assistant',
                timestamp: new Date(),
                isAssistant: true,
                messageType: 'assistant'
              }
            });
            
            // Validate the selected option to determine next state:
            // 1. If has variants → variant_selection
            // 2. If is a leaf → amount_collection
            // 3. If has children → option_selection (go deeper)
            // CRITICAL: After UPDATE_TASK_OBJECT, the task's taskId will be updated to selectedOptionId
            // So CHECK_SUBTASK_LEAF can find it by taskId, OR the handler can find by isProcessingDialogue
            console.log('🟣 [NEW_MESSAGE] Dispatching CHECK_SUBTASK_LEAF effect', {
              subtaskId: selectedOptionId,
              taskId: selectedOptionId,
              note: 'Effect handler should find task by isProcessingDialogue (taskId will be updated by UPDATE_TASK_OBJECT)'
            });
            
            effects.push({
              type: 'CHECK_SUBTASK_LEAF',
              subtaskId: selectedOptionId,
              language: context.language,
              taskId: selectedOptionId // Pass taskId so handler can find the task object by taskId (after UPDATE_TASK_OBJECT updates it)
            });
            
            console.log('🟣 [NEW_MESSAGE] Returning from leaf_validation path with', effects.length, 'effects:', effects.map((e: any) => e.type));
            
            return { nextState, nextContext, effects };
          }
        } else {
          // We're in option_selection but no option ID found - this shouldn't happen
          // But we should still return early to prevent LLM call
          return { nextState, nextContext, effects };
        }
      }
      
      // Special handling: amount input for defined tasks – numeric reply should never hit classify intent
      const definedTaskStateForAmount = context.definedTaskState || nextContext.definedTaskState;
      if (definedTaskStateForAmount === 'amount_collection') {
        const numericValue = parseFloat(msgTextTrimmed);
        const isValidNumber = !Number.isNaN(numericValue) && isFinite(numericValue) && numericValue >= 0;
        if (isValidNumber) {
          let subtaskIdForAmount = nextContext.currentAmountSubtaskId || context.currentAmountSubtaskId;
          if (!subtaskIdForAmount) {
            const sd = nextContext.subtaskData || context.subtaskData;
            if (sd && sd.selectedSubtasks && sd.currentSubtaskIndex !== undefined) {
              subtaskIdForAmount = sd.selectedSubtasks[sd.currentSubtaskIndex] || sd.currentChildId || undefined;
            }
          }
          return reduceConversation(currentState, nextContext, {
            type: 'SUBTASK_AMOUNT_PROVIDED',
            subtaskId: subtaskIdForAmount || '',
            amount: numericValue
          });
        }
      }

    }

    // Never call LLM for explicit subtask-selection messages; they are handled above when in conv_defined_task_classification.
    // If we reach here with that text (e.g. wrong state or race), do nothing — no CALL_LLM_PARSE, no "Could you please clarify".
    if (isSubtaskSelectionText) {
      return { nextState, nextContext, effects };
    }
    
    // Rule-based intent: match text to allowed intents and treat as PARSE_RESULT without calling LLM
    const allowedIntentsForRules = getAllowedIntents(currentState, context.taskState);
    const trimmedForRules = msgTextTrimmed.toLowerCase();
    // Strip trailing punctuation/space so "Nee." / "OK!" still match ^...$ patterns (else LLM may return GOODBYE → conv_bye)
    const ruleMatchText = msgTextTrimmed.trim().replace(/[\s.!?…。]+$/gu, '').toLowerCase();
    if (msgTextTrimmed.length > 0) {
      if (currentState === 'assignement_additional_comments') {
        const replyTrimmed = msgTextTrimmed.trim();
        const skipAdditionalComment =
          /^(ok|okay|oké|ja|jep|jazeker|yes|yep|yeah|y|yup|sure|nee|no|nope|nah|n|nothing|niks|geen|none|n\/a|-)$/i;
        if (skipAdditionalComment.test(ruleMatchText)) {
          return reduceConversation(currentState, nextContext, {
            type: 'PARSE_RESULT',
            messageId: event.message.id,
            intentId: 'USER_CONFIRMS',
            slots: {},
            confidence: 1
          });
        }
        return reduceConversation(currentState, nextContext, {
          type: 'PARSE_RESULT',
          messageId: event.message.id,
          intentId: 'USER_SUBMITS_ASSIGNEMENT_COMMENT',
          slots: { assignement_comment: replyTrimmed },
          confidence: 1
        });
      }
      if (allowedIntentsForRules.includes('USER_SAYS_HELLO')) {
        const helloPatterns = [
          /\bhello\b|\bhi\b|\bhey\b|\bgreetings\b/i,
          /\bgood\s+morning\b|\bgood\s+afternoon\b|\bgood\s+evening\b/i,
          /\bhallo\b|\bhoi\b|\bgoedemorgen\b|\bgoedemiddag\b|\bgoedenavond\b|\bdag\b/i
        ];
        if (helloPatterns.some((p: RegExp) => p.test(trimmedForRules))) {
          return reduceConversation(currentState, nextContext, {
            type: 'PARSE_RESULT',
            messageId: event.message.id,
            intentId: 'USER_SAYS_HELLO',
            slots: {},
            confidence: 1
          });
        }
      }
      if (allowedIntentsForRules.includes('USER_ASKS_ABOUT_DEFINED_TASKS')) {
        const wellDefinedPatterns = [
          /\bwell[- ]?defined\s+tasks?\b/i,
          /\bwhat\s+are\s+(the\s+)?(well[- ]?defined|predefined)\s+tasks?\b/i,
          /\bare\s+there\s+(any\s+)?(well[- ]?defined|predefined)\s+tasks?\b/i,
          /\b(list|show)\s+(me\s+)?(the\s+)?(well[- ]?defined|predefined)\s+tasks?\b/i,
          /\bgoed\s+gedefinieerd(e)?\s+taken?\b/i,
          /\bwat\s+zijn\s+(de\s+)?(goed\s+gedefinieerd|vooraf\s+gedefinieerd)\s+taken?\b/i,
          /\bzijn\s+er\s+(goed\s+gedefinieerd|vooraf\s+gedefinieerd)\s+taken?\b/i
        ];
        if (wellDefinedPatterns.some((p: RegExp) => p.test(trimmedForRules))) {
          return reduceConversation(currentState, nextContext, {
            type: 'PARSE_RESULT',
            messageId: event.message.id,
            intentId: 'USER_ASKS_ABOUT_DEFINED_TASKS',
            slots: {},
            confidence: 1
          });
        }
      }
      const confirmPatterns = /^(yes|yep|yeah|y|yup|ja|jep|jazeker|sure|ok|okay|oké|bevestig|confirm|akkoord|prima|goed)$/i;
      if (confirmPatterns.test(ruleMatchText) && allowedIntentsForRules.includes('USER_CONFIRMS')) {
        return reduceConversation(currentState, nextContext, {
          type: 'PARSE_RESULT',
          messageId: event.message.id,
          intentId: 'USER_CONFIRMS',
          slots: {},
          confidence: 1
        });
      }
      const rejectPatterns = /^(no|nope|nah|n|nein|nee|niet|reject|cancel|annuleer|afwijzen|geen)$/i;
      if (rejectPatterns.test(ruleMatchText)) {
        if (allowedIntentsForRules.includes('USER_REJECTS')) {
          return reduceConversation(currentState, nextContext, {
            type: 'PARSE_RESULT',
            messageId: event.message.id,
            intentId: 'USER_REJECTS',
            slots: {},
            confidence: 1
          });
        }
        if (allowedIntentsForRules.includes('USER_SAYS_NO')) {
          return reduceConversation(currentState, nextContext, {
            type: 'PARSE_RESULT',
            messageId: event.message.id,
            intentId: 'USER_SAYS_NO',
            slots: {},
            confidence: 1
          });
        }
      }
    }
    
    // Single-flight rule: only parse if not already awaiting
    if (context.awaitingParseId) {
      return { nextState, nextContext, effects };
    }
    
    // Set awaiting parse ID in context
    nextContext.awaitingParseId = event.message.id;
    
    // When in well-defined task flow, use the well-defined task blueprint state and its allowed intents
    // so classify receives e.g. taskState "subtasks_processing" and allowedIntents from the defined-task blueprint.
    const isDefinedTaskFlow = currentState === 'conv_defined_task_classification';
    const definedTaskState = context.definedTaskState;
    const allowedIntents = isDefinedTaskFlow && definedTaskState
      ? getDefinedTaskAllowedIntents(definedTaskState)
      : getAllowedIntents(currentState, context.taskState);
    const taskStateForParse = isDefinedTaskFlow && definedTaskState
      ? definedTaskState
      : context.taskState;
    const slotSchema = getSlotSchema();
    const compactContext = context.conversationId ? [] : []; // TODO: Get last 3-5 turns
    
    // Emit exactly one effect: CALL_LLM_PARSE
    effects.push({
      type: 'CALL_LLM_PARSE',
      messageId: event.message.id,
      text: event.message.text,
      conversationId: context.conversationId,
      conversationState: currentState,
      taskState: taskStateForParse as TaskDialogueState | undefined,
      allowedIntents,
      slotSchema,
      language: context.language,
      compactContext,
      timeoutMs: 10000,
      promptVersion: '1.0'
    });
    
    // Return without changing state
    return { nextState, nextContext, effects };
  }

  // Handle empty NEW_MESSAGE events
  if (event.type === 'NEW_MESSAGE' && (!event.message.text || !event.message.text.trim())) {
    return { nextState, nextContext, effects };
  }

  // Handle SUBTASK_LEAF_RESULT events
  if (event.type === 'SUBTASK_LEAF_RESULT' && currentState === 'conv_defined_task_classification') {
    const subtaskData = nextContext.subtaskData || context.subtaskData;
    
    // With the new architecture, we might not have subtaskData
    // In that case, we should still process the result if it matches the active task's taskId
    if (!subtaskData) {
      // New architecture: each subtask is its own task object
      // The task object should have taskId that matches event.subtaskId
      // We'll process it regardless - the UPDATE_TASK_OBJECT effect will find the right task
    } else {
      // Old architecture: check if this is for the current subtask or a selected child
      const currentSubtaskId = subtaskData.selectedSubtasks?.[subtaskData.currentSubtaskIndex || 0];
      const currentChildId = subtaskData.currentChildId;
      
      // Accept if it matches either the current subtask or the current child (for recursive validation)
      if (event.subtaskId !== currentSubtaskId && event.subtaskId !== currentChildId) {
        return { nextState, nextContext, effects };
      }
    }
    
    // Use the actual subtask ID from the event (could be a child if we're validating recursively)
    const actualSubtaskId = event.subtaskId;
    
    // When processing SUBTASK_LEAF_RESULT, we're always coming from leaf_validation state
    // (the leaf check was just performed), so use 'leaf_validation' as the current state
    const currentSubtaskState: SubtaskDialogueState = 'leaf_validation';
    
    // Determine the trigger based on leaf validation result
    // Priority: variants > leaf > children (go deeper)
    let trigger: SubtaskTrigger;
    
    if (event.hasVariants && event.variants) {
      // Priority 1: Has variants → trigger HAS_VARIANTS
      trigger = 'HAS_VARIANTS';
    } else if (event.isLeaf) {
      // Priority 2: Is a leaf → trigger IS_LEAF
      trigger = 'IS_LEAF';
    } else if (event.hasChildren) {
      // Priority 3: Has children → trigger HAS_CHILDREN (go deeper)
      trigger = 'HAS_CHILDREN';
    } else {
      // Fallback: treat as leaf
      trigger = 'IS_LEAF';
    }
    
    // Use the blueprint to get the next state
    const nextSubtaskState = getNextSubtaskState(currentSubtaskState, trigger);
    
    if (!nextSubtaskState) {
      return { nextState, nextContext, effects };
    }
    
    const nextDefinedTaskState = nextSubtaskState;
    nextContext.definedTaskState = nextDefinedTaskState;
    
    // When we enter amount_collection, remember which subtask we're asking the amount for
    if (nextDefinedTaskState === 'amount_collection') {
      nextContext.currentAmountSubtaskId = actualSubtaskId;
    }
    
    // Use taskId from event if available (for multi-select mode), otherwise find by isProcessingDialogue
    const targetTaskId = event.taskId;
    
    // Based on the next state, emit appropriate effects
    if (nextSubtaskState === 'variant_selection') {
      effects.push({
        type: 'RUN_ENTRY_ACTION',
        key: 'show_subtask_variant_options',
        params: { 
          subtaskId: actualSubtaskId, 
          variants: event.variants,
          parentTask: event.parentTask  // Pass parent task object with billing info
        }
      });
    } else if (nextSubtaskState === 'amount_collection') {
      // We've reached a leaf - set finalName to the current accumulated name
      console.log('📝 [NAME] Reached leaf (amount_collection) - setting finalName for taskId:', actualSubtaskId);
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: {
          finalName: undefined // Will be set from current name by effect handler
        },
        taskId: targetTaskId // Use taskId from event if available
      });
      effects.push({
        type: 'RUN_ENTRY_ACTION',
        key: 'ask_subtask_amount',
        params: { subtaskId: actualSubtaskId }
      });
    } else if (nextSubtaskState === 'option_selection') {
      // CRITICAL: Update task object's taskId to the selected option ID (narrowing down)
      // This ensures that when we check again, we're checking the right task in the tree
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: {
          dialogueState: 'option_selection',
          taskId: actualSubtaskId // Update to the selected option ID (narrowing down)
        },
        taskId: targetTaskId // Use taskId from event if available
      });
      
      // Get children for the selected option (go deeper)
      effects.push({
        type: 'GET_SUBTASK_CHILDREN',
        subtaskId: actualSubtaskId,
        language: context.language,
        taskId: targetTaskId // Pass taskId to the effect
      });
    }
    
    // Update task object with new dialogue state (if not already updated above)
    if (nextSubtaskState !== 'option_selection') {
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: {
          dialogueState: nextDefinedTaskState
        },
        taskId: targetTaskId // Use taskId from event if available
      });
    }
    
    return { nextState, nextContext, effects };
  }

  // Handle SUBTASK_VARIANT_SELECTED events
  if (event.type === 'SUBTASK_VARIANT_SELECTED' && currentState === 'conv_defined_task_classification') {
    // Guard: variantId must be an actual ID (backend key), not a human-readable label.
    // Labels like "height: 1.5 m < height < 2 m" must never overwrite taskId/subtaskId.
    const isIdLike = (v: string) => /^[\dA-Za-z_-]+$/.test(v) && !/[<>=:\s]/.test(v);
    if (!isIdLike(event.variantId)) {
      console.warn('🚫 [SUBTASK_VARIANT_SELECTED] Ignoring non-ID variantId (label-like)', {
        variantId: event.variantId,
        subtaskId: event.subtaskId
      });
      return { nextState, nextContext, effects };
    }

    const subtaskData = nextContext.subtaskData || context.subtaskData;
    if (!subtaskData || subtaskData.currentSubtaskIndex === undefined) {
      return { nextState, nextContext, effects };
    }
    
    // Update the processed subtasks with the selected variant
    // CRITICAL: Find entry by index (entries are pre-created when subtasks are submitted)
    const currentIndex = subtaskData.currentSubtaskIndex;
    const processedSubtasks = [...(subtaskData.processedSubtasks || [])]; // Create a copy
    const currentSubtaskId = subtaskData.selectedSubtasks[currentIndex];
    
    // Find entry by index (entries are pre-created in the same order as selectedSubtasks)
    let subtaskEntry: {
      subtaskId: string;
      variantId?: string;
      childId?: string;
      parentTaskId?: string;
      amount?: number;
      unit?: string;
    } | undefined = currentIndex < processedSubtasks.length 
      ? processedSubtasks[currentIndex]
      : undefined;
    
    // If not found by index (shouldn't happen), create as fallback
    if (!subtaskEntry) {
      subtaskEntry = { subtaskId: currentSubtaskId };
      processedSubtasks.push(subtaskEntry);
    }
    
    // Update subtaskId to the variantId (narrowing down to the final ID)
    // Store parent task ID for billing info lookup
    const parentTaskIdForVariant = subtaskData.currentChildId || currentSubtaskId;
    subtaskEntry.subtaskId = event.variantId; // Narrow down to variant ID
    subtaskEntry.variantId = event.variantId; // Also store as variantId for reference
    if (parentTaskIdForVariant && parentTaskIdForVariant !== event.variantId) {
      subtaskEntry.parentTaskId = parentTaskIdForVariant; // Store parent for billing lookup
    }
    
    // Update subtask data - keep currentChildId as is (it's the parent task with variants)
    // Don't overwrite it with variant ID, as we need it to fetch the parent task's billing info
    nextContext.subtaskData = {
      ...subtaskData,
      processedSubtasks: [...processedSubtasks]
      // Keep currentChildId as the parent task ID (e.g., 1820XX), not the variant ID
    };
    
    // Transition to amount_collection
    nextContext.definedTaskState = 'amount_collection';
    // Remember which subtask/variant we're collecting the amount for
    nextContext.currentAmountSubtaskId = event.variantId;
    
    // Update task object with variant ID and new dialogue state
    // We've reached a leaf - set finalName to the current accumulated name
    console.log('📝 [NAME] Reached leaf (variant selection) - setting finalName for variantId:', event.variantId);
    effects.push({
      type: 'UPDATE_TASK_OBJECT',
      updates: {
        taskId: event.variantId, // Update taskId to the selected variant ID
        dialogueState: 'amount_collection',
        finalName: undefined // Will be set from current name by effect handler
      }
    });
    
    // Ask for amount - use the variant ID as the task ID for fetching unit
    effects.push({
      type: 'RUN_ENTRY_ACTION',
      key: 'ask_subtask_amount',
      params: { subtaskId: event.variantId }  // Use variant ID instead of currentSubtaskId
    });
    
    return { nextState, nextContext, effects };
  }

  // Handle SUBTASK_AMOUNT_PROVIDED events
  if (event.type === 'SUBTASK_AMOUNT_PROVIDED' && currentState === 'conv_defined_task_classification') {
    // We’re done collecting the amount for the current subtask
    nextContext.currentAmountSubtaskId = undefined;
    const subtaskData = nextContext.subtaskData || context.subtaskData;
    
    // CRITICAL: Check if we're in multi-select mode (tasks created upfront)
    // Multi-select tasks don't use subtaskData.selectedSubtasks - they're separate task objects
    // We can detect this by checking if subtaskData exists but doesn't have selectedSubtasks
    const isMultiSelectMode = !subtaskData || !subtaskData.selectedSubtasks || subtaskData.selectedSubtasks.length === 0;
    
    if (isMultiSelectMode) {
      // MULTI-SELECT MODE: Each task is a separate object created upfront
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] ========== MULTI-SELECT MODE ==========');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Multi-select mode detected', {
        subtaskId: event.subtaskId,
        amount: event.amount,
        unit: event.unit,
        hasSubtaskData: !!subtaskData,
        hasSelectedSubtasks: !!(subtaskData?.selectedSubtasks && subtaskData.selectedSubtasks.length > 0)
      });
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Each task is a separate object - marking current task as completed');
      
      // Just mark the current task as completed - processNextPendingTask will handle the next one
      // CRITICAL: According to SubtaskClassificationDialogueBlueprint:
      // amount_collection → AMOUNT_PROVIDED → subtask_completed
      const unit = event.unit || '';
      
      const taskUpdates: any = {
        amount: event.amount,
        dialogueState: 'subtask_completed', // Update to terminal state per SubtaskClassificationDialogueBlueprint
        isProcessingDialogue: false // Mark as not processing - processNextPendingTask will pick up next
      };
      
      // Always include unit if we have it
      if (unit) {
        taskUpdates.unit = unit;
        taskUpdates.billableUnit = unit;
        taskUpdates.billableAmount = { amount: event.amount, unit: unit };
      }
      
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Updating task object with:', taskUpdates);
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Setting dialogueState to "subtask_completed" (per SubtaskClassificationDialogueBlueprint)');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Setting isProcessingDialogue = false so processNextPendingTask can find next task');
      
      // Update the current task object (UIEffectsHandler will find it by isProcessingDialogue)
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: taskUpdates
      });
      
      // Mark task as completed - this will trigger processNextPendingTask callback
      // CRITICAL: Pass taskId so handler can find the task even after isProcessingDialogue is set to false
      // CRITICAL: Use 'subtask_completed' as taskState (per SubtaskClassificationDialogueBlueprint)
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Dispatching MARK_TASK_COMPLETED effect (MULTI-SELECT mode)');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Passing taskId:', event.subtaskId, 'so handler can find the task');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Using taskState: "subtask_completed" (per SubtaskClassificationDialogueBlueprint)');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] This will trigger onTaskCompleted callback → processNextPendingTask');
      effects.push({
        type: 'MARK_TASK_COMPLETED',
        taskId: event.subtaskId, // Pass taskId so handler can find the task directly
        taskData: {
          slots: {
            amount: event.amount
          },
          taskState: 'subtask_completed' // Use subtask_completed per SubtaskClassificationDialogueBlueprint
        }
        // Don't pass wellDefinedTaskId - this will mark only the currently processing task
      });
      
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Effects dispatched - callback should trigger to process next task');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] NOT asking for location here - processNextPendingTask will check if all tasks are done');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] ========== END MULTI-SELECT MODE ==========');
      
      // CRITICAL: Don't ask for location here - processNextPendingTask will check if all tasks are done
      // and only then ask for location (or transition to final_confirmation)
      // Stay in current state - processNextPendingTask will handle the transition
      
      return { nextState, nextContext, effects };
    }
    
    // OLD SUBTASK FLOW: Use subtaskData.selectedSubtasks array
    if (!subtaskData) {
      return { nextState, nextContext, effects };
    }
    
    // Update the processed subtasks with the amount
    const processedSubtasks = [...(subtaskData.processedSubtasks || [])]; // Create a copy to avoid mutation
    const currentSubtaskId = subtaskData.selectedSubtasks?.[subtaskData.currentSubtaskIndex || 0];
    
    // Find the processed subtask entry to update
    // CRITICAL: The entry should already exist (created when subtasks were selected)
    // We should find it by the currentSubtaskId (the original subtask ID)
    // event.subtaskId might be a variant ID, but we need to find the entry by the original subtaskId
    // Find entry for this subtask path
    // Match by currentSubtaskId, currentChildId, or variantId (depending on where we are in the flow)
    let subtaskEntry = processedSubtasks.find((s: any) => 
      s.subtaskId === currentSubtaskId ||
      s.subtaskId === subtaskData.currentChildId ||
      s.subtaskId === event.subtaskId ||
      s.variantId === event.subtaskId
    );
    
    if (!subtaskEntry) {
      // Create new entry if it doesn't exist (shouldn't normally happen, but handle edge case)
      subtaskEntry = { 
        subtaskId: currentSubtaskId || event.subtaskId,
        amount: event.amount
      };
      processedSubtasks.push(subtaskEntry);
    } else {
      // Update existing entry with amount (preserve unit and other properties)
      subtaskEntry.amount = event.amount;
      // Preserve unit if it was already set (it should be set by ask_subtask_amount)
    }
    
    // Mark current subtask as completed and move to next
    const currentIndex = subtaskData.currentSubtaskIndex || 0;
    const nextIndex = currentIndex + 1;
    const totalSubtasks = subtaskData.selectedSubtasks?.length || 0;
    
    // Update subtask data with processed subtasks and new index
    // Clear currentChildId when moving to next subtask
    nextContext.subtaskData = {
      ...subtaskData,
      currentChildId: undefined, // Clear when moving to next subtask
      processedSubtasks,
      currentSubtaskIndex: nextIndex < totalSubtasks ? nextIndex : currentIndex // Only update if moving to next
    };
    
    // CRITICAL: In the new architecture, each subtask is its own task object
    // We need to update the task object's amount and unit directly (not just processedSubtasks)
    // Get the unit from the event (passed from Chat.tsx) - it should be there because ask_subtask_amount stored it
    // If not in event, try subtaskEntry, but the unit should already be on the task object from ask_subtask_amount
    const unit = event.unit || subtaskEntry.unit || '';
    
    // If unit is still empty, log a warning - it should have been set by ask_subtask_amount
    if (!unit) {
    }
    
    // Update the subtaskEntry with the unit if we have it
    if (unit && subtaskEntry) {
      subtaskEntry.unit = unit;
    }
    
    // Find the task object that corresponds to the current subtask
    // Pass taskId in the updates so UIEffectsHandler can find the correct task
    // The task object has taskId set when it's created, matching the original subtask ID
    // Build updates object - always include unit if we have it
    // Don't pass taskId in updates - UIEffectsHandler will find the task by isProcessingDialogue
    // CRITICAL: According to SubtaskClassificationDialogueBlueprint:
    // amount_collection → AMOUNT_PROVIDED → subtask_completed
    const taskUpdates: any = {
      amount: event.amount,
      dialogueState: 'subtask_completed', // Update to terminal state per SubtaskClassificationDialogueBlueprint
      processedSubtasks: processedSubtasks
    };
    
    // Always include unit if we have it
    if (unit) {
      taskUpdates.unit = unit;
      taskUpdates.billableUnit = unit;
      taskUpdates.billableAmount = { amount: event.amount, unit: unit };
    }
    
    // Also update the task object with amount, unit, and processedSubtasks
    // CRITICAL: Mark current task as NOT processing (we're done with it)
    taskUpdates.isProcessingDialogue = false;
    effects.push({
      type: 'UPDATE_TASK_OBJECT',
      updates: taskUpdates
    });
    
    if (nextIndex < totalSubtasks) {
      // Move to next subtask (location will be asked only once at the end)
      nextContext.definedTaskState = 'leaf_validation';
      
      // CRITICAL: Mark the NEXT task as processing (we're about to process it)
      // Find the next task by its original taskId (from selectedSubtasks array)
      const nextSubtaskId = subtaskData.selectedSubtasks[nextIndex];
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: {
          taskId: nextSubtaskId, // Pass the next subtask's ID so we can find it
          dialogueState: 'leaf_validation',
          isProcessingDialogue: true // Mark next task as processing
        }
      });
      
      // Check leaf status for next subtask (nextSubtaskId already declared above)
      
      // Get subtask name for the processing message
      // Get wellDefinedTaskId from context (stored when task was created)
      const wellDefinedTaskId = nextContext.wellDefinedTaskId || context.wellDefinedTaskId;
      let subtaskName = nextSubtaskId; // Fallback to ID
      
      // Try well-defined tasks first (this is the original subtask ID from selectedSubtasks)
      if (wellDefinedTaskId) {
        try {
          const subtasks = getSubtasksForTask(wellDefinedTaskId as WellDefinedTaskId, context.language as Lang);
          const currentSubtask = subtasks.find((s: any) => s.id === nextSubtaskId);
          if (currentSubtask) {
            subtaskName = currentSubtask.name || currentSubtask.description || nextSubtaskId;
            // Also update the task object's userDescription so it's available for future messages
            effects.push({
              type: 'UPDATE_TASK_OBJECT',
              updates: {
                userDescription: subtaskName !== nextSubtaskId ? subtaskName : undefined
              },
              taskId: nextSubtaskId // Specify which task to update
            });
          }
        } catch (err) {
          // Could not get subtask name
        }
      }
      
      // Add processing message for the next subtask
      // CRITICAL: Mark this as a new subtask starting for visual styling
      const langForNextSubtask = context.language || 'en';
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: t(langForNextSubtask as Lang, 'processingSubtaskNow', { taskDescription: subtaskName }),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant',
          isNewSubtask: true // Flag to indicate this is a new subtask starting
        }
      });
      
      effects.push({
        type: 'CHECK_SUBTASK_LEAF',
        subtaskId: nextSubtaskId,
        language: context.language
      });
    } else {
      // All subtasks processed for THIS task - mark it as completed
      // Let processNextPendingTask dispatch ALL_SUBTASKS_PROCESSED when every subtask is done (blueprint → defined_task_completed).
      
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] ========== OLD FLOW: All subtasks processed ==========');
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Current subtask index:', subtaskData.currentSubtaskIndex);
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Total subtasks:', totalSubtasks);
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Processed subtasks count:', processedSubtasks.length);
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] All subtasks for THIS task are now complete');
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Waiting for processNextPendingTask → ALL_SUBTASKS_PROCESSED when all subtasks are done');
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Instead, marking task as completed and letting processNextPendingTask handle next steps');
      
      // Mark the current task as completed - this will trigger processNextPendingTask callback
      // CRITICAL: Pass taskId so handler can find the task even after isProcessingDialogue is set to false
      // CRITICAL: Use 'subtask_completed' as taskState (per SubtaskClassificationDialogueBlueprint)
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Dispatching MARK_TASK_COMPLETED effect (OLD flow)');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Passing taskId:', event.subtaskId, 'so handler can find the task');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] Using taskState: "subtask_completed" (per SubtaskClassificationDialogueBlueprint)');
      console.log('✅ [SUBTASK_AMOUNT_PROVIDED] This will trigger onTaskCompleted callback → processNextPendingTask');
      effects.push({
        type: 'MARK_TASK_COMPLETED',
        taskId: event.subtaskId, // Pass taskId so handler can find the task directly
        taskData: {
          slots: {
            amount: event.amount
          },
          taskState: 'subtask_completed' // Use subtask_completed per SubtaskClassificationDialogueBlueprint
        }
        // Don't pass wellDefinedTaskId - this will mark only the currently processing task
      });
      
      // processNextPendingTask will either start the next subtask or dispatch ALL_SUBTASKS_PROCESSED (assignment location).
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] Task marked completed — processNextPendingTask drives next subtask or ALL_SUBTASKS_PROCESSED');
      console.log('📍 [SUBTASK_AMOUNT_PROVIDED] ========== END OLD FLOW ==========');
    }
    
    return { nextState, nextContext, effects };
  }

  // Handle SUBTASK_CHILDREN_RESULT events
  if (event.type === 'SUBTASK_CHILDREN_RESULT' && currentState === 'conv_defined_task_classification') {
    
    // Store children in context for lookup when user selects an option
    const subtaskData = nextContext.subtaskData || context.subtaskData;
    if (subtaskData) {
      nextContext.subtaskData = {
        ...subtaskData,
        currentChildren: event.children // Store children for option selection lookup
      };
    }
    
    // If there's only one child, automatically select it and proceed to leaf validation
    if (event.children.length === 1) {
      const singleChild = event.children[0];
      
      // Update subtask data with the selected child
      // CRITICAL: Find the entry by index (entries are pre-created when subtasks are submitted)
      const subtaskData = nextContext.subtaskData || context.subtaskData;
      if (subtaskData) {
        const processedSubtasks = [...(subtaskData.processedSubtasks || [])];
        
        // Find entry by index (entries are pre-created in the same order as selectedSubtasks)
        const entryIndex = subtaskData.currentSubtaskIndex || 0;
        let subtaskEntry = entryIndex < processedSubtasks.length 
          ? processedSubtasks[entryIndex]
          : null;
        
        // If not found by index (shouldn't happen), create as fallback
        if (!subtaskEntry) {
          const currentSubtaskId = subtaskData.selectedSubtasks?.[entryIndex];
          subtaskEntry = { subtaskId: currentSubtaskId };
          processedSubtasks.push(subtaskEntry);
        }
        
        // Update the subtaskId to the auto-selected child (narrowing down)
        // This replaces the previous subtaskId - we're narrowing down the path
        subtaskEntry.subtaskId = singleChild.id;
        
        const updatedSubtaskData = {
          ...subtaskData,
          currentChildId: singleChild.id,
          processedSubtasks: processedSubtasks
        };
        nextContext.subtaskData = updatedSubtaskData;
        
        // Also update the task object
        effects.push({
          type: 'UPDATE_TASK_OBJECT',
          updates: {
            processedSubtasks: processedSubtasks
          }
        });
      }
      
      // Transition to leaf_validation and automatically check the child
      nextContext.definedTaskState = 'leaf_validation';
      
      // Update task object with new dialogue state
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates: {
          dialogueState: 'leaf_validation'
        }
      });
      
      // Get child description for the processing message
      // Use GLOBAL language setting - tasks don't have their own language
      const globalLang = (context.language || 'en') as Lang;
      const childName = globalLang === 'nl'
        ? (singleChild.dutch_description || singleChild.description || singleChild.id)
        : (singleChild.description || singleChild.id);
      
      // Update task object's taskId, userDescription, and name
      // Append to name path: "mainName; name2; name3"
      const nameUpdate = childName !== singleChild.id ? childName : undefined;
      console.log('📝 [NAME] Auto-selected child - updating name:', { childId: singleChild.id, childName, nameUpdate, willAppend: !!nameUpdate });
      const updates: any = {
        taskId: singleChild.id, // Update to the child ID
      };
      if (nameUpdate) {
        updates.userDescription = nameUpdate;
        updates.name = nameUpdate; // Will be appended to existing name by effect handler
      }
      effects.push({
        type: 'UPDATE_TASK_OBJECT',
        updates
      });
      
      // Add processing message for the auto-selected child
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: t(globalLang, 'processingSubtaskNow', { taskDescription: childName }),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant'
        }
      });
      
      // Automatically validate the single child (recursive)
      effects.push({
        type: 'CHECK_SUBTASK_LEAF',
        subtaskId: singleChild.id,
        language: context.language
      });
      
      return { nextState, nextContext, effects };
    }
    
    // Multiple children - show selection UI
    nextContext.definedTaskState = 'option_selection';
    
    // Update task object with new dialogue state
    effects.push({
      type: 'UPDATE_TASK_OBJECT',
      updates: {
        dialogueState: 'option_selection'
      }
    });
    
    effects.push({
      type: 'RUN_ENTRY_ACTION',
      key: 'show_subtask_children_options',
      params: { subtaskId: event.subtaskId, children: event.children }
    });
    
    return { nextState, nextContext, effects };
  }

  // Defined-task blueprint: subtasks_processing + ALL_SUBTASKS_PROCESSED → defined_task_completed (location from assignment)
  if (
    event.type === 'PARSE_RESULT' &&
    event.intentId === 'ALL_SUBTASKS_PROCESSED' &&
    currentState === 'conv_defined_task_classification'
  ) {
    const dts = context.definedTaskState || nextContext.definedTaskState;
    // Subtask flow leaves definedTaskState as amount_collection (or similar) after the last amount;
    // blueprint terminal step is still ALL_SUBTASKS_PROCESSED → defined_task_completed.
    if (
      dts === 'defined_task_completed' ||
      dts === 'subtasks_selection' ||
      dts === 'defined_task_aborted'
    ) {
      return { nextState, nextContext, effects };
    }
    const locationValue = defaultTaskLocationFromContext(context);
    nextContext.slots = { ...nextContext.slots, location: locationValue };
    const nextDefined = getNextDefinedTaskState('subtasks_processing', 'ALL_SUBTASKS_PROCESSED' as DefinedTaskTrigger);
    if (nextDefined) {
      nextContext.definedTaskState = nextDefined;
    }
    const wellDefinedTaskId = nextContext.wellDefinedTaskId || context.wellDefinedTaskId;
    effects.push({
      type: 'UPDATE_TASK_OBJECT',
      updates: {
        location: locationValue,
        dialogueState: 'defined_task_completed',
        isSubmitted: true,
        isProcessingDialogue: false,
      },
      wellDefinedTaskId,
    });
    effects.push({
      type: 'MARK_TASK_COMPLETED',
      wellDefinedTaskId,
      taskData: {
        slots: { location: locationValue },
        taskState: 'defined_task_completed',
      },
    });
    return { nextState, nextContext, effects };
  }

  // 2. Handle PARSE_RESULT
  if (event.type === 'PARSE_RESULT') {
    // When in well-defined task flow, use the defined-task blueprint allowed intents for validation
    const isDefinedTaskFlow = currentState === 'conv_defined_task_classification';
    const definedTaskStateForParse = context.definedTaskState;
    const allowedIntents = isDefinedTaskFlow && definedTaskStateForParse
      ? getDefinedTaskAllowedIntents(definedTaskStateForParse)
      : getAllowedIntents(currentState, context.taskState);
    
    // For task-level intents, allow direct PARSE_RESULT without awaitingParseId
    const taskMappedIntent = context.taskState ? mapEventToTaskTrigger(event) : null;
    const isTaskLevelIntent =
      !!context.taskState && taskMappedIntent !== null && allowedIntents.includes(event.intentId);

    // Conversation-level intents: allow when not in task subflow, in defined-task flow, or intent does not map to a task trigger
    // (avoids dropping rule-based USER_SAYS_NO while stale taskState is still set after task completion)
    const isAllowedConversationIntent =
      allowedIntents.includes(event.intentId) &&
      (!context.taskState || isDefinedTaskFlow || taskMappedIntent === null);
    
    // Drop if messageId doesn't match (unless it's a task-level intent or allowed conversation-level intent)
    if (!isTaskLevelIntent && !isAllowedConversationIntent && event.messageId !== context.awaitingParseId) {
      return { nextState, nextContext, effects };
    }
    
    // Clear awaiting parse ID
    nextContext.awaitingParseId = undefined;
    
    // Merge slots into context first
    if (event.slots.task_description) {
      nextContext.slots.task_description = event.slots.task_description;
    }
    if (event.slots.variant) {
      nextContext.slots.variant = event.slots.variant;
    }
    if (event.slots.amount) {
      nextContext.slots.amount = event.slots.amount;
    }
    if (event.slots.location) {
      nextContext.slots.location = event.slots.location;
    }
    if (event.slots.confirmation !== undefined) {
      nextContext.slots.confirmation = event.slots.confirmation;
    }
    if (event.slots.assignement_comment) {
      nextContext.slots.assignement_comment = event.slots.assignement_comment;
    }
    
    // Prioritize task intents while in task subflow for a simpler, predictable pipeline
    // 1) If a task subflow is active, try mapping to a task trigger first
    let taskTrigger: TaskTrigger | null = null;
    if (context.taskState) {
      taskTrigger = taskMappedIntent;
      if (taskTrigger) {
        // Run task reducer immediately and return
        const taskResult = reduceTaskFlow(context.taskState, nextContext, event);
        nextContext.taskState = taskResult.nextState;
        nextContext.taskContext = taskResult.nextContext;
        effects.push(...taskResult.effects);

        // If task reached a terminal, translate to a conversation trigger
        if (taskResult.parentTrigger) {
          const convTrigger = taskResult.parentTrigger as ConversationTrigger;
          const newState = getNextConversationState(currentState, convTrigger);
          if (newState) {
            nextState = newState;
            nextContext.taskState = undefined;

            const metadata = getConversationStateMetadata(nextState);
            if (metadata.entryPromptKey) {
              effects.push({
                type: 'ADD_MESSAGE',
                message: {
                  id: Math.random().toString(36).substr(2, 9),
                  text: '',
                  sender: 'Assistant',
                  timestamp: new Date(),
                  isAssistant: true,
                  messageType: 'assistant',
                  textKey: metadata.entryPromptKey
                }
              });
            }
          }
        }

        return { nextState, nextContext, effects };
      }
    }

    // 2) Otherwise, handle as a conversation-level intent
    // Reuse allowedIntents already computed above
    let conversationTrigger: ConversationTrigger | null = null;
    if (allowedIntents.includes(event.intentId)) {
      conversationTrigger = mapEventToTrigger(event);
    }
    
    // If low confidence or not allowed, emit ASK_CLARIFY and keep state
    if (!conversationTrigger && !taskTrigger) {
      const langForClarify = context.language || 'en';
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: t(langForClarify as Lang, 'couldYouPleaseClarify'),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant'
        }
      });
      return { nextState, nextContext, effects };
    }
    
    // 3. Apply transitions
    if (conversationTrigger) {
      // Apply conversation state transition
      let newState = getNextConversationState(currentState, conversationTrigger);
      
      // Special handling: For well-defined tasks in conv_defined_task_classification,
      // ALL_TASKS_COMPLETED should go to final_confirmation (skip are_there_more_tasks)
      if (conversationTrigger === 'ALL_TASKS_COMPLETED' && currentState === 'conv_defined_task_classification') {
        newState = 'final_confirmation';
      }
      
      // Check if this is a well-defined task BEFORE going to task splitting
      if (conversationTrigger === 'USER_SUBMITS_TASK' && newState === 'conv_task_simple_splitting_and_confirmation') {
        const taskDescription = event.slots?.task_description || '';
        if (taskDescription && isWellDefinedTask(taskDescription, context.language as Lang)) {
          newState = 'conv_defined_task_classification';
          
          // CRITICAL: Create ONE initial task object (A) for the well-defined task
          // This will be updated or duplicated when options are selected
          const wellDefinedTaskId = getWellDefinedTaskId(taskDescription, context.language as Lang);
          if (wellDefinedTaskId) {
            // Set defined task state in context
            nextContext.definedTaskState = 'subtasks_selection';
            
            // Store wellDefinedTaskId in context for later use (to get subtask names, etc.)
            nextContext.wellDefinedTaskId = wellDefinedTaskId;
            
            // Create the initial task object A with the well-defined task info
            // Get the well-defined task name for the initial description
            let initialTaskName = taskDescription;
            try {
              const wellDefinedTasks = getWellDefinedTasks(context.language as Lang);
              const matchedTask = wellDefinedTasks.find((t: any) => t.id === wellDefinedTaskId);
              if (matchedTask) {
                initialTaskName = matchedTask.name || taskDescription;
              }
            } catch (err) {
              // Use taskDescription as fallback
            }
            
            const initialTaskObject = {
              id: Math.random().toString(36).substr(2, 9),
              taskId: wellDefinedTaskId, // Start with well-defined task ID
              userDescription: initialTaskName,
              name: initialTaskName,
              finalName: undefined as string | undefined,
              dialogueState: 'option_selection' as const, // Will show options
              isProcessingDialogue: true, // Active while showing options
              isSubmitted: false,
              wellDefinedTaskId: wellDefinedTaskId
            };
            
            console.log('➕ [TASK_CREATION] [REDUCER] Creating initial task object A', { 
              id: initialTaskObject.id, 
              taskId: initialTaskObject.taskId, 
              name: initialTaskName,
              wellDefinedTaskId,
              dialogueState: initialTaskObject.dialogueState
            });
            
            // Add the initial task object to conversation
            // NOTE: Parent removal will be handled in UIEffectsHandler when tasks are added
            effects.push({
              type: 'ADD_TASKS_TO_CONVERSATION',
              tasks: [initialTaskObject]
            });
          }
        }
      }
      
      // Conversation state transition (logging removed)
      
      if (newState) {
        nextState = newState;
        if (
          newState === 'are_there_more_tasks' ||
          newState === 'final_confirmation' ||
          newState === 'assignement_additional_comments'
        ) {
          nextContext.taskState = undefined;
        }

        // CRITICAL: Only emit entry actions and prompts on ACTUAL state transitions
        // If newState === currentState, we're staying in the same state (e.g., viewing an old conversation)
        // Entry actions should only run when entering a state, not when already in it
        const isActualTransition = newState !== currentState;
        
        if (isActualTransition) {
          // Emit UPDATE_STATE effect to persist the state change
          effects.push({
            type: 'UPDATE_STATE',
            state: nextState
          });
          
          // Add entry prompt effect
          const metadata = getConversationStateMetadata(nextState);
          
          // IMPORTANT: Add ADD_TASKS_TO_CONVERSATION effects BEFORE entry actions
          // This ensures task objects are available when entry actions try to find them
          // (This is already done above for well-defined tasks, but we handle it here for consistency)
          
          if (metadata.entryPromptKey) {
            effects.push({
              type: 'ADD_MESSAGE',
              message: {
                id: Math.random().toString(36).substr(2, 9),
                text: '', // Will be filled by effects runner
                sender: 'Assistant',
                timestamp: new Date(),
                isAssistant: true,
                messageType: 'assistant',
                textKey: metadata.entryPromptKey
              }
            });
          }
          
          // Add entry action for final_confirmation to show task summary
          if (newState === 'final_confirmation') {
            effects.push({
              type: 'RUN_ENTRY_ACTION',
              key: 'show_task_summary',
              params: undefined
            });
          }

          if (
            newState === 'conv_bye' &&
            currentState === 'assignement_additional_comments' &&
            conversationTrigger === 'USER_SUBMITS_ASSIGNEMENT_COMMENT'
          ) {
            const commentText = event.slots?.assignement_comment;
            if (typeof commentText === 'string' && commentText.trim()) {
              effects.push({
                type: 'RUN_ENTRY_ACTION',
                key: 'append_assignement_comment',
                params: { text: commentText.trim() }
              });
            }
          }

          // Add entry actions AFTER task objects have been added
          if (metadata.entryActionKeys) {
            metadata.entryActionKeys.forEach(key => {
              // Pass wellDefinedTaskId to show_defined_task_message entry action
              const params = key === 'show_defined_task_message' && nextContext.wellDefinedTaskId
                ? { wellDefinedTaskId: nextContext.wellDefinedTaskId }
                : undefined;
              
              effects.push({
                type: 'RUN_ENTRY_ACTION',
                key,
                params
              });
            });
          }
        } else {
          // Same-state transition (e.g. USER_SAYS_HELLO in conv_welcome → conv_welcome).
          // Still run greet_user so the user gets a hello back when they say hello.
          if (conversationTrigger === 'USER_SAYS_HELLO' && currentState === 'conv_welcome') {
            effects.push({
              type: 'RUN_ENTRY_ACTION',
              key: 'greet_user',
              params: undefined
            });
          }
          effects.push({
            type: 'UPDATE_STATE',
            state: nextState
          });
        }

        // Special handling for task splitting and confirmation
        if (newState === 'conv_task_simple_splitting_and_confirmation') {
          if (event.slots?.task_description) {
            effects.push({
              type: 'PROCESS_TASK_SPLITTING',
              taskText: event.slots.task_description,
              conversationState: newState,
              taskState: context.taskState,
              context: nextContext,
              conversationId: context.conversationId,
              language: context.language
            });
          }
        }

        // If we just entered task processing, initialize the TASK subflow
        if (newState === 'conv_task_processing' && !context.taskState) {
          const initialTaskState: TaskDialogueState = 'processing_options_selection';
          nextContext.taskState = initialTaskState;
          nextContext.taskContext = { slots: {} };

          // Emit the task state's entry prompt immediately so the user sees the next step
          const taskMeta = getTaskStateMetadata(initialTaskState);
          if (taskMeta.entryEffectKey) {
            effects.push({
              type: 'ADD_MESSAGE',
              message: {
                id: Math.random().toString(36).substr(2, 9),
                text: '',
                sender: 'Assistant',
                timestamp: new Date(),
                isAssistant: true,
                messageType: 'assistant',
                textKey: taskMeta.entryEffectKey
              }
            });
          }
          // Also run any entry actions for the task state (kicks off processing)
          if (taskMeta.entryActionKeys) {
            taskMeta.entryActionKeys.forEach(key => {
              effects.push({
                type: 'RUN_ENTRY_ACTION',
                key,
                params: undefined
              });
            });
          }
        }
      }
    }
    
    return { nextState, nextContext, effects };
  }

  // 3b. Well-defined task: variant selection via USER_SELECTION (button click)
  // Chat dispatches USER_SELECTION with type 'variant'; we must convert to SUBTASK_VARIANT_SELECTED
  // so the defined-task reducer runs and emits ask_subtask_amount etc.
  if (event.type === 'USER_SELECTION' && currentState === 'conv_defined_task_classification') {
    const definedTaskState = nextContext.definedTaskState ?? context.definedTaskState;
    if (definedTaskState === 'variant_selection' && event.selection.type === 'variant' && event.selection.id) {
      const subtaskData = nextContext.subtaskData ?? context.subtaskData;
      if (subtaskData?.selectedSubtasks?.length && subtaskData.currentSubtaskIndex !== undefined) {
        const currentSubtaskId = subtaskData.selectedSubtasks[subtaskData.currentSubtaskIndex];
        return reduceConversation(currentState, nextContext, {
          type: 'SUBTASK_VARIANT_SELECTED',
          subtaskId: currentSubtaskId,
          variantId: event.selection.id
        });
      }
    }
  }

  // 4. Handle structured UI events (USER_SELECTION, etc.)
  if (event.type === 'USER_SELECTION') {
    // Map directly to TaskTrigger
    const taskTrigger = mapEventToTaskTrigger(event);
    
    if (taskTrigger && context.taskState) {
      const taskResult = reduceTaskFlow(context.taskState, nextContext, event);
      nextContext.taskState = taskResult.nextState;
      nextContext.taskContext = taskResult.nextContext;
      effects.push(...taskResult.effects);
      
      // If task returns a terminal, translate to conversation trigger
      if (taskResult.parentTrigger) {
        const conversationTrigger = taskResult.parentTrigger as ConversationTrigger;
        const newState = getNextConversationState(currentState, conversationTrigger);
        if (newState) {
          nextState = newState;
          nextContext.taskState = undefined; // Clear task state
          
          // Add entry prompt effect
          const metadata = getConversationStateMetadata(nextState);
          if (metadata.entryPromptKey) {
            effects.push({
              type: 'ADD_MESSAGE',
              message: {
                id: Math.random().toString(36).substr(2, 9),
                text: '', // Will be filled by effects runner
                sender: 'Assistant',
                timestamp: new Date(),
                isAssistant: true,
                messageType: 'assistant',
                textKey: metadata.entryPromptKey
              }
            });
          }
        }
      }
    }
    
    return { nextState, nextContext, effects };
  }

  // Numeric amount during task-processing amount_clarification should bypass classify intent
  if (
    event.type === 'NEW_MESSAGE' &&
    currentState === 'conv_task_processing' &&
    context.taskState === 'amount_clarification' &&
    event.message.text &&
    event.message.text.trim()
  ) {
    const amountText = event.message.text.trim();
    const numericValue = parseFloat(amountText);
    const isValidNumber = !Number.isNaN(numericValue) && isFinite(numericValue) && numericValue >= 0;
    if (isValidNumber) {
      return reduceConversation(currentState, nextContext, {
        type: 'USER_SELECTION',
        selection: { type: 'amount', value: numericValue }
      });
    }
  }

  // Handle other event types (TASK_COMPLETED, TASK_ABORTED, etc.)
  const trigger = mapEventToTrigger(event);
  if (trigger) {
    const newState = getNextConversationState(currentState, trigger);
    if (newState) {
      nextState = newState;
      
      // Add entry prompt and entry action effects
      const metadata = getConversationStateMetadata(nextState);
      if (metadata.entryPromptKey) {
        effects.push({
          type: 'ADD_MESSAGE',
          message: {
            id: Math.random().toString(36).substr(2, 9),
            text: '', // Will be filled by effects runner
            sender: 'Assistant',
            timestamp: new Date(),
            isAssistant: true,
            messageType: 'assistant',
            textKey: metadata.entryPromptKey
          }
        });
      }
      
      // Add entry action effects (e.g., submit_conversation for conv_bye)
      if (metadata.entryActionKeys) {
        metadata.entryActionKeys.forEach(key => {
          effects.push({
            type: 'RUN_ENTRY_ACTION',
            key: key,
            params: undefined
          });
        });
      }
    }
  }

  // Handle TASK_SPLITTING_RESULT
  if (event.type === 'TASK_SPLITTING_RESULT') {
    // Ensure we stay in the task confirmation state
    nextState = 'conv_task_simple_splitting_and_confirmation';
    
    // Store the processed tasks in context for later use
    nextContext.processedTasks = event.tasks;
    
    // Add tasks to conversation
    effects.push({
      type: 'ADD_TASKS_TO_CONVERSATION',
      tasks: event.tasks
    });
    
    // Add messages to show the processed tasks
    const langForTasks = context.language || 'en';
    if (event.tasks.length === 1) {
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: t(langForTasks as Lang, 'tasksCompletedSingle'),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant'
        }
      });
    } else {
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: t(langForTasks as Lang, 'tasksCompletedMultiple', { count: event.tasks.length }),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant'
        }
      });
    }

    // Add individual task messages
    event.tasks.forEach((task: any) => {
      effects.push({
        type: 'ADD_MESSAGE',
        message: {
          id: Math.random().toString(36).substr(2, 9),
          text: `• ${task.userDescription}`,
          sender: 'AI Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistantClassification',
          taskId: task.id,
          keywords: task.keywords,
          actions: task.actions,
          measurements: task.measurements,
          location: task.location,
          employee_count: task.employeeCount
        }
      });
    });

    // Add confirmation message
    const langForConfirm = context.language || 'en';
    effects.push({
      type: 'ADD_MESSAGE',
      message: {
        id: Math.random().toString(36).substr(2, 9),
        text: t(langForConfirm as Lang, 'confirmOrRetypeTasks'),
        sender: 'Assistant',
        timestamp: new Date(),
        isAssistant: true,
        messageType: 'assistant'
      }
    });
  }

  // Handle TASK_SPLITTING_ERROR
  if (event.type === 'TASK_SPLITTING_ERROR') {
    // Task splitting failed
    const langForError = context.language || 'en';
    effects.push({
      type: 'ADD_MESSAGE',
      message: {
        id: Math.random().toString(36).substr(2, 9),
        text: t(langForError as Lang, 'taskSplittingError', { error: event.error }),
        sender: 'Assistant',
        timestamp: new Date(),
        isAssistant: true,
        messageType: 'assistant'
      }
    });
  }

  // Handle PARSE_ERROR - only for the *current* in-flight parse.
  // If a stale LLM call fails after we've already moved on (e.g. user selected an option/subtask),
  // we ignore it so we don't suddenly show "Could you please clarify?" out of context.
  if (event.type === 'PARSE_ERROR') {
    // Drop stale errors that don't match the currently awaited message
    if (event.messageId && context.awaitingParseId && event.messageId !== context.awaitingParseId) {
      return { nextState, nextContext, effects };
    }

    nextContext.awaitingParseId = undefined;
    const langForError = context.language || 'en';
    effects.push({
      type: 'ADD_MESSAGE',
      message: {
        id: Math.random().toString(36).substr(2, 9),
        text: t(langForError as Lang, 'couldYouPleaseClarify'),
        sender: 'Assistant',
        timestamp: new Date(),
        isAssistant: true,
        messageType: 'assistant'
      }
    });
  }

  return { nextState, nextContext, effects };
}
